import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import Button from '@/components/Button.tsx';
import { TypoBody, TypoCallout, TypoCaption, TypoHeading2 } from '@/components/Typography.tsx';
import LADDER_LEADERBOARD from '@/graphql/queries/ladder-leaderboard.graphql';
import LADDER_PLAYER_SEARCH from '@/graphql/queries/ladder-player-search.graphql';

import {
  LadderProfile, LadderStats, PublicPlayer, TONE_TEXT, formatStreak, getIdleTone, useLadderProfile,
} from './common.ts';
import { AppNote, Badge, EmptyState, InlineLoader, Modal, Panel, PlayerAvatar, Table } from './components.tsx';

const PAGE_SIZE = 50;

interface ChallengePreview {
  available: boolean;
  unavailableReason: string | null;
  isRankWar: boolean;
  climbIfWin: number | null;
  rankIfWin: number | null;
}

interface LadderRow {
  isMe: boolean;
  player: PublicPlayer;
  challenge?: ChallengePreview | null;
}

const LadderRankings = () => {
  const { profile, stats } = useLadderProfile();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const query = useDebounced(search.trim(), 300);
  const [highlightMe, setHighlightMe] = useState(false);

  const jumpToMyRung = () => {
    if (!stats?.rank) return;
    setSearch('');
    setPage(Math.floor((stats.rank - 1) / PAGE_SIZE));
    setHighlightMe(true);
  };

  return (
    <div className="flex flex-col gap-6">
      <RankHud profile={profile} stats={stats} />
      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-3 flex-1">
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search players by ladder name…"
              className="w-full max-w-xs pl-4 pr-1 py-2 rounded bg-neutral text-callout text-neutral-text-strong
                         placeholder:text-neutral-placeholder outline-none focus:ring-2 focus:ring-primary
                         m:max-w-none m:text-body
                         [&::-webkit-search-cancel-button]:!w-6 [&::-webkit-search-cancel-button]:!h-6"
            />
            {stats?.rank && (
              <Button color="secondary" onClick={jumpToMyRung} className="py-2">
                Jump to my rank (#{stats.rank})
              </Button>
            )}
          </div>
          {!query && stats && (
            <Pager page={page} total={stats.rankedPlayers} onChange={setPage} />
          )}
        </div>
        {query
          ? <SearchResults query={query} myId={profile?.clientId} />
          : (
            <LeaderboardPage
              page={page}
              highlightMe={highlightMe}
              onHighlighted={() => setHighlightMe(false)}
            />
          )}
      </Panel>
    </div>
  );
};

const RankHud = ({ profile, stats }: { profile?: LadderProfile; stats?: LadderStats }) => {
  const [rulesOpen, setRulesOpen] = useState(false);

  return (
    <Panel className="flex flex-wrap items-center justify-between gap-6">
      <div className="flex items-center gap-5">
        <div className="w-16 h-16 shrink-0 rounded-lg bg-neutral3 flex flex-col items-center justify-center">
          <strong className="text-heading2 leading-none text-neutral3-text-strong">{stats?.rank ?? '–'}</strong>
          <TypoCaption className="uppercase font-semibold text-neutral3-text-soft">Rank</TypoCaption>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <TypoHeading2 className="text-neutral2-text-strong font-semibold m:text-heading2-m">
              {profile?.ladderName ?? '—'}
            </TypoHeading2>
            <Badge tone="primary">You</Badge>
            {(stats?.streak ?? 0) > 0 && <Badge tone="warning">{formatStreak(stats?.streak ?? 0)}</Badge>}
          </div>
          {stats && (
            <TypoCallout className="text-neutral2-text-soft">
              Record: <strong className={TONE_TEXT.success}>{stats.wins} W</strong>
              {' · '}<strong className={TONE_TEXT.danger}>{stats.losses} L</strong>
              {' · '}Days idle:{' '}
              <strong className={TONE_TEXT[getIdleTone(stats.daysIdle)]}>{stats.daysIdle ?? '—'}</strong>
            </TypoCallout>
          )}
          <TypoCaption className="text-neutral2-text-softer">
            How high can you climb? Challenge someone above you on the ladder in the app.
          </TypoCaption>
        </div>
      </div>
      <Button color="secondary" onClick={() => setRulesOpen(true)}>How to climb the ladder</Button>
      <Modal open={rulesOpen} title="How to climb the ladder" onClose={() => setRulesOpen(false)}>
        <div className="flex flex-col gap-4">
          <TypoBody className="text-neutral-text">
            Beat a player ranked above you in a ranked match and you climb half the distance between your
            rank and theirs, rounded up:
          </TypoBody>
          <div className="px-4 py-3 rounded bg-neutral2 text-center font-semibold text-neutral2-text-strong">
            Climb = ⌈ (your rank − their rank) ÷ 2 ⌉
          </div>
          <TypoBody className="text-neutral-text">
            The climb is locked in when the challenge is sent, and the Challenge column below shows the rank each
            win would take you to. A player directly above you can’t turn down your challenge: that’s a rank war.
          </TypoBody>
          <TypoBody className="text-neutral-text">
            Stay active: ranks drop after 7 days without a match.
          </TypoBody>
        </div>
      </Modal>
    </Panel>
  );
};

const Pager = ({ page, total, onChange }: { page: number; total: number; onChange: (page: number) => void }) => {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total ? page * PAGE_SIZE + 1 : 0;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className="flex items-center gap-2">
      <Button color="secondary" className="py-2" disabled={page === 0} onClick={() => onChange(page - 1)}>
        Prev
      </Button>
      <TypoCallout className="text-neutral2-text-soft whitespace-nowrap">
        {from}–{to} of {total}
      </TypoCallout>
      <Button color="secondary" className="py-2" disabled={page >= pages - 1} onClick={() => onChange(page + 1)}>
        Next
      </Button>
    </div>
  );
};

const LeaderboardPage = ({ page, highlightMe, onHighlighted }: {
  page: number;
  highlightMe: boolean;
  onHighlighted: () => void;
}) => {
  const { data, loading } = useQuery(LADDER_LEADERBOARD, {
    variables: { limit: PAGE_SIZE, offset: page * PAGE_SIZE },
  });
  const rows: LadderRow[] = data?.ladderLeaderboard ?? [];

  if (loading && !rows.length) return <InlineLoader />;
  if (!rows.length) return <EmptyState text="No ranked players yet." />;

  return <RankingsTable rows={rows} highlightMe={highlightMe} onHighlighted={onHighlighted} />;
};

const SearchResults = ({ query, myId }: { query: string; myId?: number }) => {
  const { data, loading } = useQuery(LADDER_PLAYER_SEARCH, { variables: { query, limit: PAGE_SIZE } });
  const rows: LadderRow[] = (data?.ladderPlayerSearch ?? []).map((player: PublicPlayer) => ({
    player,
    isMe: player.clientId === myId,
  }));

  if (loading && !rows.length) return <InlineLoader />;
  if (!rows.length) return <EmptyState text={`No players match “${query}”.`} />;

  return <RankingsTable rows={rows} searching />;
};

const RankingsTable = ({ rows, searching = false, highlightMe = false, onHighlighted }: {
  rows: LadderRow[];
  searching?: boolean;
  highlightMe?: boolean;
  onHighlighted?: () => void;
}) => {
  const myRow = useRef<HTMLTableRowElement>(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (!highlightMe || !myRow.current) return;
    myRow.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPulse(true);
    onHighlighted?.();
  }, [highlightMe, onHighlighted, rows]);

  useEffect(() => {
    if (!pulse) return;
    const timeout = setTimeout(() => setPulse(false), 2500);
    return () => clearTimeout(timeout);
  }, [pulse]);

  return (
    <>
      <Table
        head={[
          { label: 'Rank', className: 'text-center w-16' },
          { label: 'Player' },
          { label: 'Won', className: 'text-center' },
          { label: 'Lost', className: 'text-center' },
          { label: 'Streak', className: 'text-center' },
          { label: 'Days idle', className: 'text-center' },
          { label: searching ? '' : 'Challenge', className: 'text-right' },
        ]}
      >
        {rows.map(({ isMe, player, challenge }) => (
          <tr
            key={player.clientId}
            ref={isMe ? myRow : undefined}
            className={`transition-shadow duration-500
              ${isMe ? 'bg-neutral3' : 'hover:bg-shade-5'} ${isMe && pulse ? 'ring-2 ring-inset ring-primary' : ''}`}
          >
            <td className="px-3 py-3 text-center">
              <strong className={`text-heading italic ${RANK_COLORS[player.rank ?? 0] ?? 'text-neutral2-text-soft'}`}>
                {player.rank ? String(player.rank).padStart(2, '0') : '—'}
              </strong>
            </td>
            <td className="px-3 py-3">
              <div className="flex items-center gap-3 min-w-[10rem]">
                <PlayerAvatar player={player} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <TypoCallout className="truncate font-semibold text-neutral2-text-strong">
                      {player.ladderName}
                    </TypoCallout>
                    {isMe && <Badge tone="primary">You</Badge>}
                  </div>
                  {player.rank === 1 && (
                    <TypoCaption className="text-warning font-semibold">👑 Ladder leader</TypoCaption>
                  )}
                </div>
              </div>
            </td>
            <td className={`px-3 py-3 text-center font-semibold ${TONE_TEXT.success}`}>{player.wins}</td>
            <td className={`px-3 py-3 text-center font-semibold ${TONE_TEXT.danger}`}>{player.losses}</td>
            <td className="px-3 py-3 text-center whitespace-nowrap">
              {player.streak > 0 ? <Badge tone="warning">{formatStreak(player.streak)}</Badge> : (
                <TypoCaption className="text-neutral2-text-softer">{formatStreak(player.streak)}</TypoCaption>
              )}
            </td>
            <td className={`px-3 py-3 text-center whitespace-nowrap ${TONE_TEXT[getIdleTone(player.daysIdle)]}`}>
              {player.daysIdle === null ? '—' : `${player.daysIdle} ${player.daysIdle === 1 ? 'day' : 'days'}`}
            </td>
            <td className="px-3 py-3 text-right">
              {!searching && <ChallengeCell isMe={isMe} challenge={challenge} />}
            </td>
          </tr>
        ))}
      </Table>
      {!searching && <AppNote className="block mt-4" />}
    </>
  );
};

const RANK_COLORS: Record<number, string> = { 1: 'text-warning', 2: 'text-neutral2-text-strong', 3: 'text-pink' };

const ChallengeCell = ({ isMe, challenge }: { isMe: boolean; challenge?: ChallengePreview | null }) => {
  if (isMe) return <TypoCaption className="italic text-primary font-semibold">Your placement</TypoCaption>;
  if (!challenge) return <TypoCaption className="text-neutral2-text-weak">—</TypoCaption>;
  if (!challenge.available) {
    return <Badge>{challenge.unavailableReason ?? 'Busy'}</Badge>;
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Badge tone="success">Win → rank {challenge.rankIfWin ?? '—'}</Badge>
      {challenge.isRankWar && <Badge tone="warning">Rank war</Badge>}
    </div>
  );
};

function useDebounced<T>(value: T, delay: number) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);

  return debounced;
}

export default LadderRankings;
